import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Download, RefreshCw } from 'lucide-react';
import { api, ApiError } from '../../api/cliente.js';
import { useSessao } from '../../auth/SessaoContexto.js';
import { Botao } from '../../componentes/ui/Botao.js';
import { Modal } from '../../componentes/ui/Modal.js';
import { Pill, type TomPill } from '../../componentes/ui/Pill.js';

interface Particao {
  ponto: string;
  totalBytes: number;
  usadoBytes: number;
  livreBytes: number;
  usoPercent: number;
}

interface Diagnostico {
  alcancavel: boolean;
  erro?: string;
  coletadoEm: string;
  sistema: {
    kernel: string | null;
    distro: string | null;
    uptimeSegundos: number | null;
    carga: [number, number, number] | null;
    memoriaTotalBytes: number | null;
    memoriaDisponivelBytes: number | null;
    cpus: number | null;
  };
  discos: Particao[];
  containers: { nome: string; estado: string; saude: string | null; desde: string }[];
  galera: {
    tamanho: number | null;
    statusComponente: string | null;
    estadoLocal: string | null;
    flowControlPausado: number | null;
    filaRecebimento: number | null;
  } | null;
  malha: { chave: string; endereco: string; ultimoHandshake: number | null }[];
  arbitro: { unidadeAtiva: boolean } | null;
  backup: {
    estado: string | null;
    quando: string | null;
    detalhe: string | null;
    arquivos: number;
    bytes: number;
    proximaExecucao: string | null;
  } | null;
  certificado: { expiraEm: string | null; diasRestantes: number | null } | null;
}

interface ArquivoBackup {
  nome: string;
  origem: string;
  bytes: number;
  modificadoEm: string;
  banco: string | null;
}

interface Props {
  noId: string;
  nome: string;
  hostname: string;
  aoFechar: () => void;
}

/** Acima disto o disco deixa de ser informação e passa a ser risco. */
const DISCO_ALERTA = 85;
/** O WireGuard renova a cada ~2 min; o dobro disso é sinal de malha caída. */
const HANDSHAKE_ALERTA = 240;

export function ModalDetalhesNo({ noId, nome, hostname, aoFechar }: Props) {
  const qc = useQueryClient();
  const { pode } = useSessao();

  const { data: d, isFetching, refetch } = useQuery({
    queryKey: ['infra', 'diagnostico', noId],
    queryFn: () => api.get<Diagnostico>(`/infra/nos/${noId}/diagnostico`),
    refetchInterval: 30_000,
  });

  const { data: backups = [] } = useQuery({
    queryKey: ['infra', 'backups', noId],
    queryFn: () => api.get<ArquivoBackup[]>(`/infra/nos/${noId}/backups`),
  });

  const gerar = useMutation({
    mutationFn: () => api.post<{ ok: boolean; saida: string }>(`/infra/nos/${noId}/backups`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['infra', 'backups', noId] });
      void qc.invalidateQueries({ queryKey: ['infra', 'diagnostico', noId] });
    },
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="largo"
      titulo={nome}
      descricao={hostname}
      rodape={
        <>
          <Botao
            onClick={() => void refetch()}
            carregando={isFetching}
            iconeEsquerda={<RefreshCw size={14} />}
          >
            Atualizar
          </Botao>
          <Botao variante="primario" onClick={aoFechar}>
            Fechar
          </Botao>
        </>
      }
    >
      {!d ? (
        <div className="h-40 animate-pulse rounded-card bg-borda-tenue" />
      ) : !d.alcancavel ? (
        <p className="flex items-start gap-2 rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
          <AlertTriangle size={15} className="mt-px shrink-0" />
          Não foi possível falar com o nó: {d.erro ?? 'motivo desconhecido'}
        </p>
      ) : (
        <div className="space-y-5">
          <Secao titulo="Sistema">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-3">
              <Item rotulo="Distro" valor={d.sistema.distro ?? '—'} />
              <Item rotulo="Kernel" valor={d.sistema.kernel ?? '—'} mono />
              <Item rotulo="No ar há" valor={duracao(d.sistema.uptimeSegundos)} />
              <Item
                rotulo="Carga"
                valor={d.sistema.carga ? d.sistema.carga.join('  ') : '—'}
                mono
                // A carga sozinha não diz nada: 4.0 é tranquilo em 8 núcleos e
                // grave em 2. O alerta compara com o que o nó tem.
                alerta={!!d.sistema.carga && !!d.sistema.cpus && d.sistema.carga[0] > d.sistema.cpus}
                nota={d.sistema.cpus ? `${d.sistema.cpus} núcleos` : undefined}
              />
              <Item
                rotulo="Memória livre"
                valor={
                  d.sistema.memoriaDisponivelBytes && d.sistema.memoriaTotalBytes
                    ? `${bytes(d.sistema.memoriaDisponivelBytes)} de ${bytes(d.sistema.memoriaTotalBytes)}`
                    : '—'
                }
              />
            </div>
          </Secao>

          <Secao titulo="Disco">
            <div className="space-y-2.5">
              {d.discos.map((p) => (
                <div key={p.ponto}>
                  <div className="mb-1 flex justify-between text-[12.5px]">
                    <span className="font-mono text-texto-suave">{p.ponto}</span>
                    <span className={p.usoPercent >= DISCO_ALERTA ? 'font-bold text-erro' : ''}>
                      {bytes(p.livreBytes)} livres · {p.usoPercent}%
                    </span>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-borda-suave">
                    <div
                      className={`h-full rounded-full ${
                        p.usoPercent >= DISCO_ALERTA ? 'bg-erro' : 'bg-barra-ok'
                      }`}
                      style={{ width: `${Math.min(p.usoPercent, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Secao>

          {d.containers.length > 0 && (
            <Secao titulo="Containers">
              <div className="flex flex-wrap gap-1.5">
                {d.containers.map((c) => (
                  <Pill
                    key={c.nome}
                    tom={
                      c.saude === 'unhealthy' || c.estado !== 'running'
                        ? 'erro'
                        : c.saude === 'starting'
                          ? 'aviso'
                          : 'ok'
                    }
                  >
                    {c.nome.replace(/^rdns-/, '')}
                    {c.saude && c.saude !== 'healthy' ? ` · ${c.saude}` : ''}
                  </Pill>
                ))}
              </div>
            </Secao>
          )}

          {d.galera && (
            <Secao titulo="Galera">
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-3">
                <Item rotulo="Membros" valor={String(d.galera.tamanho ?? '—')} />
                <Item
                  rotulo="Componente"
                  valor={d.galera.statusComponente ?? '—'}
                  alerta={d.galera.statusComponente?.toLowerCase() !== 'primary'}
                />
                <Item
                  rotulo="Estado local"
                  valor={d.galera.estadoLocal ?? '—'}
                  alerta={d.galera.estadoLocal !== 'Synced'}
                />
                <Item
                  rotulo="Flow control"
                  valor={`${((d.galera.flowControlPausado ?? 0) * 100).toFixed(1)}%`}
                  // Acima de 5% o nó está segurando o grupo inteiro: os outros
                  // esperam por ele para manter a replicação em dia.
                  alerta={(d.galera.flowControlPausado ?? 0) > 0.05}
                  nota="tempo pausado"
                />
              </div>
            </Secao>
          )}

          {d.arbitro && (
            <Secao titulo="Árbitro">
              <Item
                rotulo="Serviço garbd"
                valor={d.arbitro.unidadeAtiva ? 'ativo' : 'parado'}
                alerta={!d.arbitro.unidadeAtiva}
              />
            </Secao>
          )}

          {d.malha.length > 0 && (
            <Secao titulo="Malha WireGuard">
              <div className="space-y-1.5">
                {d.malha.map((p) => {
                  const atras = p.ultimoHandshake
                    ? Math.floor(Date.now() / 1000) - p.ultimoHandshake
                    : null;
                  return (
                    <div key={p.chave} className="flex justify-between gap-3 text-[12.5px]">
                      <span className="truncate font-mono text-texto-suave">
                        {p.endereco || p.chave}
                      </span>
                      <span
                        className={
                          atras === null || atras > HANDSHAKE_ALERTA ? 'font-bold text-erro' : ''
                        }
                      >
                        {atras === null ? 'sem handshake' : `há ${duracao(atras)}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Secao>
          )}

          {d.certificado && (
            <Secao titulo="Certificado TLS">
              <Item
                rotulo="Expira em"
                valor={
                  d.certificado.diasRestantes !== null
                    ? `${d.certificado.diasRestantes} dias`
                    : '—'
                }
                // O Caddy renova aos 30 dias do fim. Abaixo de 15 a renovação
                // já deveria ter acontecido — é sinal de que algo travou.
                alerta={(d.certificado.diasRestantes ?? 99) < 15}
              />
            </Secao>
          )}

          <Secao
            titulo="Backup"
            acao={
              <Botao
                tamanho="tabela"
                onClick={() => gerar.mutate()}
                carregando={gerar.isPending}
                title="Roda o backup agora, sem esperar o horário agendado"
              >
                Gerar agora
              </Botao>
            }
          >
            {!d.backup ? (
              <p className="rounded-chip bg-aviso-fundo px-3 py-2 text-[12.5px] font-semibold text-aviso">
                Backup não instalado neste nó. Ver docs/backup-e-restauracao.md, seção 3.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-3">
                <Item
                  rotulo="Última execução"
                  valor={d.backup.estado ?? '—'}
                  alerta={d.backup.estado !== 'ok'}
                  nota={d.backup.quando ? quando(d.backup.quando) : undefined}
                />
                <Item
                  rotulo="Próxima"
                  valor={d.backup.proximaExecucao ? quando(d.backup.proximaExecucao) : 'sem timer'}
                  alerta={!d.backup.proximaExecucao}
                />
                <Item
                  rotulo="Guardado"
                  valor={`${d.backup.arquivos} arquivos · ${bytes(d.backup.bytes)}`}
                />
              </div>
            )}

            {d.backup?.detalhe && d.backup.estado !== 'ok' && (
              <p className="mt-2 rounded-chip bg-erro-fundo px-3 py-2 text-[12px] text-erro">
                {d.backup.detalhe}
              </p>
            )}

            {gerar.error && (
              <p className="mt-2 rounded-chip bg-erro-fundo px-3 py-2 text-[12px] font-semibold text-erro">
                {(gerar.error as ApiError).descricao}
              </p>
            )}
            {gerar.data && (
              <pre className="mt-2 max-h-32 overflow-auto rounded-chip bg-borda-tenue px-3 py-2 font-mono text-[11.5px] leading-relaxed">
                {gerar.data.saida}
              </pre>
            )}

            {backups.length > 0 && (
              <div className="mt-3 max-h-64 overflow-auto rounded-chip border border-borda">
                <table className="w-full border-collapse text-[12px]">
                  <tbody>
                    {backups.map((a) => (
                      <tr key={`${a.origem}/${a.nome}`} className="border-b border-borda-suave last:border-0">
                        <td className="px-3 py-2">
                          <span className="font-mono">{a.nome}</span>
                          <span className="ml-2 text-texto-suave">{a.origem}</span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right text-texto-suave">
                          {bytes(a.bytes)}
                        </td>
                        <td className="w-10 px-3 py-2 text-right">
                          {pode('backup:baixar') && (
                            // Link direto, e não fetch para um blob: o arquivo
                            // pode ter centenas de megabytes, e montá-lo na
                            // memória do navegador travaria a aba. O cookie de
                            // sessão acompanha por ser mesma origem.
                            <a
                              href={`/api/infra/nos/${noId}/backups/${encodeURIComponent(a.nome)}`}
                              download
                              title={`Baixar ${a.nome}`}
                              className="text-texto-suave hover:text-marca"
                            >
                              <Download size={15} />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {pode('backup:baixar') && backups.length > 0 && (
              <p className="mt-2 text-[11.5px] leading-relaxed text-texto-suave">
                O arquivo contém e-mails, hashes de senha e as zonas de todos os clientes. Todo
                download fica registrado na auditoria.
              </p>
            )}
          </Secao>

          <p className="text-[11px] text-texto-mudo">Coletado {quando(d.coletadoEm)}.</p>
        </div>
      )}
    </Modal>
  );
}

function Secao({
  titulo,
  acao,
  children,
}: {
  titulo: string;
  acao?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">{titulo}</p>
        {acao}
      </div>
      {children}
    </section>
  );
}

function Item({
  rotulo,
  valor,
  mono,
  alerta,
  nota,
}: {
  rotulo: string;
  valor: string;
  mono?: boolean;
  alerta?: boolean;
  nota?: string;
}) {
  return (
    <div>
      <p className="text-[11.5px] text-texto-suave">{rotulo}</p>
      <p
        className={`truncate text-[13px] font-semibold ${mono ? 'font-mono' : ''} ${
          alerta ? 'text-erro' : ''
        }`}
      >
        {valor}
      </p>
      {nota && <p className="text-[11px] text-texto-mudo">{nota}</p>}
    </div>
  );
}

function bytes(n: number | null): string {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function duracao(segundos: number | null): string {
  if (segundos === null) return '—';
  const d = Math.floor(segundos / 86400);
  const h = Math.floor((segundos % 86400) / 3600);
  const m = Math.floor((segundos % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m}min`;
}

function quando(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return iso;
  return data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}
